/**
 * PGlite adapter for desktop mode
 *
 * This adapter provides PGlite (PostgreSQL in WebAssembly) for offline-first desktop functionality.
 * Uses kysely-pglite package for simple integration.
 */

import os from 'node:os';
import path from 'node:path';
import { fileURLToPath, URL as NodeURL } from 'node:url';
import type { PGlite } from '@electric-sql/pglite';
import type { DatabaseConnection, Kysely, QueryResult, TransactionSettings } from 'kysely';
import { CompiledQuery, PostgresAdapter, PostgresIntrospector, PostgresQueryCompiler } from 'kysely';
import { z } from 'zod';
import { env } from '@/env';
import { runtime } from '@/lib/runtime';
import { readSystemEnvString } from '@/lib/utils/env';
import type { DB } from './schema';

const waitImmediate = async (): Promise<void> => {
  await Promise.resolve();
};

const pgLiteQueryResultSchema = z.object({
  rowCount: z.number().optional(),
  rows: z.array(z.unknown()).optional(),
});

const parseQueryResultMetadata = (value: unknown) => {
  const parsed = pgLiteQueryResultSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

const resolveRowCount = (value: unknown): number | undefined => parseQueryResultMetadata(value)?.rowCount;

const resolveRows = <T>(value: unknown): T[] => {
  const metadata = parseQueryResultMetadata(value);
  if (!metadata?.rows) {
    return [];
  }
  // PGlite returns unknown row shapes; trust the Kysely type parameter provided by callers.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return metadata.rows as unknown as T[];
};

// Platform-specific data directory
function getDataDirectory(): string {
  const platform = process.platform;
  const appName = 'com.sweetistics.app';

  if (platform === 'darwin') {
    return path.join(os.homedir(), 'Library', 'Application Support', appName, 'db');
  }

  if (platform === 'win32') {
    const fallback = path.join(os.homedir(), 'AppData', 'Roaming');
    const appData = readSystemEnvString('APPDATA', fallback) ?? fallback;
    return path.join(appData, 'Sweetistics', 'db');
  }

  // Linux
  const xdgFallback = path.join(os.homedir(), '.local', 'share');
  const xdgData = readSystemEnvString('XDG_DATA_HOME', xdgFallback) ?? xdgFallback;
  return path.join(xdgData, 'sweetistics', 'db');
}

const DEFAULT_SCHEMA_PATH = (() => {
  // Avoid evaluating Node-specific URL helpers in browser bundles; this module should only run in desktop mode.
  if ('window' in globalThis) {
    return 'apps/desktop/schema-desktop-pglite.sql';
  }

  try {
    const moduleUrl = typeof import.meta.url === 'string' ? import.meta.url : undefined;
    if (moduleUrl?.startsWith('file:')) {
      const schemaUrl = new NodeURL('../../apps/desktop/schema-desktop-pglite.sql', moduleUrl);
      return fileURLToPath(schemaUrl);
    }
  } catch (error) {
    console.warn('[PGlite] Falling back to cwd schema resolution:', error);
  }

  // Fallback for bundlers that do not provide file: URLs via import.meta.url (e.g. Turbopack preview).
  return path.resolve(process.cwd(), 'apps/desktop/schema-desktop-pglite.sql');
})();

// Minimal driver lifted from kysely-pglite to avoid bundling optional dialect deps in the desktop build.
class PgliteConnection implements DatabaseConnection {
  private readonly client: PGlite;

  constructor(client: PGlite) {
    this.client = client;
  }

  async executeQuery<R>(compiledQuery: CompiledQuery): Promise<QueryResult<R>> {
    const result = await this.client.query(compiledQuery.sql, [...compiledQuery.parameters]);
    const rowCount = resolveRowCount(result);
    const rows = resolveRows<R>(result);
    if (typeof rowCount === 'number') {
      return {
        rows,
        numAffectedRows: BigInt(rowCount),
      } satisfies QueryResult<R>;
    }
    return { rows } satisfies QueryResult<R>;
  }

  async *streamQuery<R>(compiledQuery: CompiledQuery): AsyncIterableIterator<QueryResult<R>> {
    // PGlite does not expose streaming cursors; fall back to executing the full query once.
    yield await this.executeQuery<R>(compiledQuery);
  }
}

class PgliteDriver {
  private readonly client: PGlite;

  constructor(client: PGlite) {
    this.client = client;
  }

  async init(): Promise<void> {
    await waitImmediate();
  }

  async acquireConnection(): Promise<DatabaseConnection> {
    await waitImmediate();
    return new PgliteConnection(this.client);
  }

  async beginTransaction(connection: DatabaseConnection, _settings: TransactionSettings): Promise<void> {
    await connection.executeQuery(CompiledQuery.raw('BEGIN'));
  }

  async commitTransaction(connection: DatabaseConnection): Promise<void> {
    await connection.executeQuery(CompiledQuery.raw('COMMIT'));
  }

  async rollbackTransaction(connection: DatabaseConnection): Promise<void> {
    await connection.executeQuery(CompiledQuery.raw('ROLLBACK'));
  }

  async releaseConnection(_connection: DatabaseConnection): Promise<void> {
    await waitImmediate();
  }

  async destroy(): Promise<void> {
    await this.client.close();
  }
}

// Global singleton
let pgliteKyselyInstance: Kysely<DB> | null = null;
let pgliteClient: PGlite | null = null;

const resolvePgliteStorageMode = (): 'memory' | 'persistent' => {
  const raw = env.SWEETISTICS_PGLITE_MODE?.trim().toLowerCase();
  return raw === 'persistent' ? 'persistent' : 'memory';
};

const isPgliteMemoryMode = (): boolean => resolvePgliteStorageMode() === 'memory';

/**
 * Run schema migration if database is empty
 * Uses PGlite-compatible schema without citext extension
 */
async function runSchemaMigration(client: PGlite): Promise<void> {
  try {
    // Check if database already has tables
    const result = await client.query<{ count: string | number }>(
      "SELECT COUNT(*) as count FROM information_schema.tables WHERE table_schema = 'public'"
    );
    const tableCount = Number(result.rows[0]?.count) || 0;

    if (tableCount > 0) {
      console.log(`[PGlite] Schema already loaded (${tableCount} tables), skipping migration`);
      return;
    }

    console.log('[PGlite] Loading schema from schema-desktop-pglite.sql...');

    // Load schema file
    const fs = await import('node:fs/promises');
    // Prefer explicit override, otherwise load the bundled schema via the static file URL so Turbopack can track it.
    const rawSchemaPath = env.SWEETISTICS_SCHEMA_PATH?.trim();
    const environmentSchemaPath = rawSchemaPath && rawSchemaPath.length > 0 ? rawSchemaPath : null;
    const schema =
      environmentSchemaPath && environmentSchemaPath.length > 0
        ? await fs.readFile(environmentSchemaPath, 'utf8')
        : await fs.readFile(DEFAULT_SCHEMA_PATH, 'utf8');

    // Execute entire schema at once (PGlite can handle it)
    await client.exec(schema);

    console.log('[PGlite] ✅ Schema migration complete!');

    // Verify tables were created
    const verifyResult = await client.query<{ count: string | number }>(
      "SELECT COUNT(*) as count FROM information_schema.tables WHERE table_schema = 'public'"
    );
    const newTableCount = Number(verifyResult.rows[0]?.count) || 0;
    console.log(`[PGlite] Created ${newTableCount} tables`);
  } catch (error) {
    console.error('[PGlite] Schema migration failed:', error);
    throw error;
  }
}

/**
 * Create PGlite-backed Kysely instance
 */
export async function createPGliteKysely(): Promise<Kysely<DB>> {
  if (!runtime.isDesktop) {
    throw new Error('createPGliteKysely is only supported in desktop mode.');
  }
  if (pgliteKyselyInstance) {
    return pgliteKyselyInstance;
  }

  // Use in-memory mode for development to avoid WASM corruption from hot reloads.
  // Toggle via SWEETISTICS_PGLITE_MODE=persistent to opt into durable storage.
  const useMemoryMode = isPgliteMemoryMode();
  const dataDirectory = useMemoryMode ? null : getDataDirectory();

  if (useMemoryMode) {
    console.log('[PGlite] Initializing in MEMORY mode');
  } else {
    console.log('[PGlite] Initializing in PERSISTENT mode', { dataDirectory });
  }

  try {
    // Create PGlite dialect (in-memory for dev stability)
    console.log('[PGlite] Creating PGlite instance...');
    const { PGlite: PGliteModule } = await import('@electric-sql/pglite');
    if (dataDirectory) {
      const fs = await import('node:fs/promises');
      await fs.mkdir(dataDirectory, { recursive: true });
      pgliteClient = await PGliteModule.create({ dataDir: dataDirectory });
    } else {
      pgliteClient = await PGliteModule.create();
    }
    console.log('[PGlite] Database ready');

    const { Kysely: KyselyConstructor, CamelCasePlugin } = await import('kysely');

    const client = pgliteClient;
    if (!client) {
      throw new Error('PGlite client failed to initialize');
    }

    pgliteKyselyInstance = new KyselyConstructor<DB>({
      dialect: {
        createAdapter: () => new PostgresAdapter(),
        createDriver: () => new PgliteDriver(client),
        createIntrospector: (database) => new PostgresIntrospector(database),
        createQueryCompiler: () => new PostgresQueryCompiler(),
      },
      plugins: [new CamelCasePlugin()],
    });

    // Run schema migration (uses PGlite-compatible schema without citext)
    await runSchemaMigration(pgliteClient);

    return pgliteKyselyInstance;
  } catch (error) {
    console.error('[PGlite] Initialization failed:', error);
    if (error instanceof Error) {
      console.error('[PGlite] Error details:', {
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 5).join('\n'),
      });
    }
    throw error;
  }
}

/**
 * Close PGlite database
 */
export async function closePGlite(): Promise<void> {
  if (pgliteKyselyInstance) {
    await pgliteKyselyInstance.destroy();
    pgliteKyselyInstance = null;
  }

  if (pgliteClient) {
    await pgliteClient.close();
    pgliteClient = null;
  }
}
